import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from '../core/api.service';
import { AuthService } from '../core/auth.service';
import { ToastService } from '../core/toast.service';

type Panel = 'change-password' | 'forgot-password' | 'change-username' | 'forgot-username';

interface Option { key: Panel; title: string; icon: string; color: string; blurb: string; }

/**
 * Security and Passwords — a 4-option dashboard. Each option opens its own panel underneath.
 * URL: /security (tiles only) or /security/<panel> so a panel can be linked or refreshed directly.
 */
@Component({
  selector: 'app-security',
  template: `
  <h1 class="page-title">Security and Passwords</h1>
  <p class="muted">Manage how you sign in to your account.</p>

  <div class="tiles sec-tiles">
    <a class="tile" *ngFor="let o of options" [class.on]="panel === o.key" [routerLink]="['/security', o.key]">
      <div class="disc" [ngClass]="o.color">{{ o.icon }}</div>
      <div class="tile-label">{{ o.title }}</div>
      <div class="tile-sub">{{ o.blurb }}</div>
    </a>
  </div>

  <div class="alert info" *ngIf="auth.session?.mustChangePassword && panel !== 'change-password'">
    Please set your own password first — open <b>Change Password</b>.
  </div>

  <!-- 1. Change Password -->
  <app-change-password *ngIf="panel === 'change-password'"></app-change-password>

  <!-- 2. Forgot Password -->
  <div class="panel narrow" *ngIf="panel === 'forgot-password'">
    <div class="panel-head">Forgot Password</div>
    <div class="panel-body">
      <p>Don't remember your current password? We'll email a secure link to <b>{{ me?.email }}</b>.
         Open it within 60 minutes to set a new password — you don't need the old one.</p>
      <div class="alert ok" *ngIf="resetSent">{{ resetSent }}</div>
      <button class="btn primary" (click)="sendReset()" [disabled]="busy || !me">{{ busy ? 'Sending…' : 'Email Me a Reset Link' }}</button>
      <p class="muted small" style="margin-top:14px">Not receiving it? Check your Spam folder, or ask an administrator to reset your password.</p>
    </div>
  </div>

  <!-- 3. Change Username and Email -->
  <div class="panel narrow" *ngIf="panel === 'change-username'">
    <div class="panel-head">Change Username and Email</div>
    <form class="panel-body form-rows" #af="ngForm" (ngSubmit)="saveAccount()" *ngIf="acc">
      <div class="row"><label class="req">Username :</label>
        <div>
          <input name="username" [(ngModel)]="acc.username" required maxlength="150" (blur)="checkUsername()" (ngModelChange)="userCheck = null" autocomplete="username">
          <div class="hint-line" *ngIf="!userCheck">3–30 characters: letters, numbers, dot, underscore or hyphen.</div>
          <div class="field-ok" *ngIf="userCheck?.available">✓ {{ userCheck.message }}</div>
          <div class="field-err" *ngIf="userCheck && !userCheck.available">{{ userCheck.message }}</div>
        </div>
      </div>
      <div class="row"><label class="req">Email :</label>
        <input name="email" type="email" [(ngModel)]="acc.email" required email autocomplete="email">
      </div>
      <div class="row"><label class="req">Current Password :</label>
        <div>
          <input name="pwd" type="password" [(ngModel)]="acc.currentPassword" required autocomplete="current-password">
          <div class="hint-line">Needed to confirm it's really you.</div>
        </div>
      </div>
      <div class="row"><label></label>
        <button class="btn primary" [disabled]="busy || !af.valid || (userCheck && !userCheck.available)">{{ busy ? 'Saving…' : 'UPDATE USERNAME & EMAIL' }}</button>
      </div>
    </form>
  </div>

  <!-- 4. Forgot User Name or Email -->
  <div class="panel narrow" *ngIf="panel === 'forgot-username'">
    <div class="panel-head">Forgot User Name or Email</div>
    <div class="panel-body">
      <p>These are the details you sign in with — use either one:</p>
      <div class="cred-box" *ngIf="me">
        <div><span>Username</span><b>{{ me.username }}</b></div>
        <div><span>Email</span><b>{{ me.email }}</b></div>
      </div>
      <div class="alert ok" *ngIf="reminderSent">{{ reminderSent }}</div>
      <button class="btn" (click)="sendReminder()" [disabled]="busy || !me">{{ busy ? 'Sending…' : 'Email Me These Details' }}</button>
      <p class="muted small" style="margin-top:14px">Signed out and can't remember them? Use <b>Forgot username or email?</b> on the sign-in page.</p>
    </div>
  </div>`
})
export class SecurityComponent implements OnInit {
  options: Option[] = [
    { key: 'change-password', title: 'Change Password', icon: '🔒', color: 'teal', blurb: 'Set a new password using your current one' },
    { key: 'forgot-password', title: 'Forgot Password', icon: '🔑', color: 'orange', blurb: 'Get a reset link by email' },
    { key: 'change-username', title: 'Change Username and Email', icon: '👤', color: 'blue', blurb: 'Update the details you sign in with' },
    { key: 'forgot-username', title: 'Forgot User Name or Email', icon: '✉', color: 'dark', blurb: 'See or email your sign-in details' }
  ];
  panel: Panel = null;
  me: any = null;
  acc: any = null;
  userCheck: { available: boolean; message: string } = null;
  busy = false;
  resetSent = '';
  reminderSent = '';

  constructor(private api: ApiService, public auth: AuthService, private toast: ToastService,
              private route: ActivatedRoute, private router: Router) {}

  ngOnInit() {
    this.route.paramMap.subscribe(p => {
      const key = p.get('panel') as Panel;
      this.panel = this.options.some(o => o.key === key) ? key : null;
      this.resetSent = this.reminderSent = '';
      this.userCheck = null;
      if (this.panel === 'change-username') { this.acc = this.me ? { username: this.me.username, email: this.me.email } : null; }
    });
    this.loadMe();
  }

  private loadMe() {
    this.api.get('/profile').subscribe(p => {
      this.me = p;
      if (this.panel === 'change-username') { this.acc = { username: p.username, email: p.email }; }
    });
  }

  sendReset() {
    this.busy = true;
    this.api.post('/auth/forgot-password', { usernameOrEmail: this.me.username }).subscribe(
      () => { this.busy = false; this.resetSent = 'Done — a reset link is on its way to ' + this.me.email + '.'; },
      e => { this.busy = false; this.toast.err(ApiService.message(e)); });
  }

  checkUsername() {
    const u = (this.acc.username || '').trim();
    if (!u || u.toLowerCase() === (this.me.username || '').toLowerCase()) { this.userCheck = null; return; }
    this.api.get('/auth/username-available', { username: u }).subscribe(r => this.userCheck = r, () => this.userCheck = null);
  }

  saveAccount() {
    this.busy = true;
    this.api.put('/profile/account', this.acc).subscribe(r => {
      this.busy = false;
      this.toast.ok(r.message);
      this.acc.currentPassword = '';
      this.userCheck = null;
      this.loadMe();
      this.auth.refresh().subscribe();
    }, e => { this.busy = false; this.toast.err(ApiService.message(e)); });
  }

  sendReminder() {
    this.busy = true;
    this.api.post('/auth/forgot-username', { emailOrPhone: this.me.email }).subscribe(
      () => { this.busy = false; this.reminderSent = 'Done — your sign-in details were emailed to ' + this.me.email + '.'; },
      e => { this.busy = false; this.toast.err(ApiService.message(e)); });
  }
}

/** Public page (signed out): "Forgot username or email?" link on the sign-in page. */
@Component({
  selector: 'app-forgot-username',
  styles: ['.auth-banner { display: block; width: 100%; max-width: 100%; height: auto; margin: 0 0 6px; }'],
  template: `
  <div class="auth-wrap">
    <div class="auth-card wide">
      <img class="auth-banner" src="assets/header-banner.png" alt="AAA Academy · College Campus Online">
      <div class="auth-narrow">
      <h2>Forgot User Name or Email</h2>
      <p class="muted small">Enter the email address <b>or</b> cell phone number on your account. We'll email your username and
        the email you registered with.</p>
      <form (ngSubmit)="submit()" #f="ngForm">
        <label>Email or Cell Phone</label>
        <input name="v" [(ngModel)]="value" required placeholder="e.g. jdoe@company.com or 9045550142">
        <div class="alert ok" *ngIf="sent">{{ sent }}</div>
        <div class="alert err" *ngIf="error">{{ error }}</div>
        <button class="btn primary block" [disabled]="busy || !f.valid">{{ busy ? 'Sending…' : 'Email My Sign-in Details' }}</button>
      </form>
      <p class="center"><a routerLink="/forgot-password">Forgot password instead?</a> · <a routerLink="/signin">Back to sign in</a></p>
      </div>
    </div>
  </div>`
})
export class ForgotUsernameComponent {
  value = '';
  busy = false;
  sent = '';
  error = '';
  constructor(private api: ApiService) {}
  submit() {
    this.busy = true; this.error = ''; this.sent = '';
    this.api.post('/auth/forgot-username', { emailOrPhone: this.value.trim() }).subscribe(
      r => { this.busy = false; this.sent = r.message; },
      e => { this.busy = false; this.error = ApiService.message(e); });
  }
}
